/**
 * How a map's nodes are stored in its Y.Doc, and where each node shows.
 * Plan: docs/plans/2026-10-02-crdt-schema-v2.md.
 *
 * Schema v1 (`metadata.schemaVersion` unset): `nodes` Y.Map<id, {…fields,
 * parentId, childIds}>, each node written whole by every edit.
 *
 * Schema v2 (`metadata.schemaVersion === 2`):
 * - `nodesV2` Y.Map<id, Y.Map>: one key per field, so concurrent edits of
 *   different fields both stay. A node's place is `parentId` + `order` (an
 *   order key, engine/sync/order-key.js), held by the node only: no list of
 *   children anywhere to overwrite.
 * - `deletedNodes` Y.Map<id, deletedAt>: ids of deleted nodes. A node whose
 *   parent (or an ancestor) is deleted is deleted too, even if it was moved
 *   or added there at the same time (decision 3: the deletion wins).
 * - `collapsed` and the measured sizes are kept per device (LOCAL_FIELDS),
 *   not in the map.
 * - `nodes` (v1) stays as it was at the migration, and a change an older
 *   client brings late is folded in (map-nodes-v2.js `foldLateV1`). Two
 *   weeks after the migration this copy is dropped
 *   (docs/plans/2026-10-13-drop-schema-v1.md); late changes still bring
 *   entries back, folded and dropped again.
 *
 * Reading never writes: a node whose parent is missing shows under the root
 * until the parent arrives, and a cycle (two nodes moved under each other at
 * once) shows its smallest id under the root. Every copy with the same data
 * shows the same tree.
 */
import * as Y from "yjs";
import { compareSiblings, isValidOrderKey } from "./order-key.js";

export const SCHEMA_V2 = 2;
export const NODES_V1 = "nodes";
export const NODES_V2 = "nodesV2";
export const DELETED_NODES = "deletedNodes";
export const V2_MIGRATIONS = "v2Migrations";
/**
 * Who made a change (map history, docs/architecture/map-history.md): the
 * server records the editing session (a number it gives out) each change
 * arrived on; `editSessions` (Y.Map) says, signed with the account's signing
 * key (engine/crypto/signing-key.js), which account each session is:
 * `"<session>:<Yjs client id>"` → `{ user, signature }`. Written by the app
 * (yjs-doc.js `_nameSession`), inside the map like everything else.
 */
export const EDIT_SESSIONS = "editSessions";
/**
 * Document notes deleted (Y.Map: note id → when): a deletion wins over a
 * change made at the same time, as for nodes (`deletedNodes`, decision 3 of
 * the schema v2 plan; Alex, 2026-10-26: "delete wins ... just make it
 * coherent"). A rename replaces the note's entry: one made while someone
 * deleted the note left a new entry, and the note came back.
 */
export const DELETED_DOC_NOTES = "deletedDocNotes";

/** The map's session entries: `{ session, client, user, signature }`. */
export function readEditSessions(doc) {
  const out = [];
  if (!doc.share.has(EDIT_SESSIONS)) return out;
  doc.getMap(EDIT_SESSIONS).forEach((value, key) => {
    const [session, client] = String(key).split(":").map(Number);
    if (value && typeof value.user === "string" && typeof value.signature === "string"
        && Number.isSafeInteger(session) && Number.isSafeInteger(client)) {
      out.push({ session, client, user: value.user, signature: value.signature });
    }
  });
  return out;
}

/**
 * The format a client reports for a map (heartbeat `schema`, the server's
 * count of maps by format): 1, 2 (v2 still holding the old copy), or this
 * (v2 without it). The map itself stays `schemaVersion` 2.
 */
export const REPORTED_V2_WITHOUT_OLD_COPY = 3;

/** Kept per device in schema v2 (decision 1; sizes depend on fonts). */
export const LOCAL_FIELDS = new Set(["collapsed", "mmWidth", "mmHeight", "mmTextHash"]);
/** Never stored as fields of a v2 node: the tree's shape, and the key. */
export const STRUCTURE_FIELDS = new Set(["id", "children", "childIds"]);

/**
 * Converts a nested tree node to a flat node for Y.Map storage
 * @param {Object} node - Tree node with children array
 * @param {string|null} parentId - Parent node ID
 * @returns {Object} Flat node with parentId and childIds
 */
/**
 * Fields an interface adds ("holon:effort", engine/index.js checks their
 * names and sizes): kept through every conversion here, as the fields listed
 * below are. Read: any key with a colon, a cheap test on every node.
 */
export function copyInterfaceFields(from, to) {
  for (const key in from) {
    if (key.indexOf(":") > 0 && from[key] != null) to[key] = from[key];
  }
  return to;
}

export function treeNodeToFlat(node, parentId = null) {
  return copyInterfaceFields(node, {
    id: node.id,
    parentId: parentId,
    childIds: (node.children || []).map((c) => c.id),
    name: node.name || "",
    color: node.color || null,
    notes: node.notes || [],
    createdAt: node.createdAt || null,
    // Task properties
    status: node.status || null,
    startDate: node.startDate || null,
    endDate: node.endDate || null,
    duration: node.duration || null,
    effort: node.effort || null,
    effortUnit: node.effortUnit || null,
    priority: node.priority || null,
    assignee: node.assignee || null,
    link: node.link || null,
    // Rough duration { value, unit } — see engine/core/estimate.js
    estimate: node.estimate || null,
    // Bubble positioning (relative to parent)
    x: node.x,
    y: node.y,
    // Mindmap positioning
    mmX: node.mmX,
    mmY: node.mmY,
    mmSide: node.mmSide,
    // Mindmap offset positioning (for shared docs)
    mmOffsetX: node.mmOffsetX,
    mmOffsetY: node.mmOffsetY,
    // Mindmap dimension caching (prevents infinite recalculation loop)
    mmWidth: node.mmWidth,
    mmHeight: node.mmHeight,
    mmTextHash: node.mmTextHash,
    // UI state
    collapsed: node.collapsed,
    // Metadata for conflict tracking
    _lastEditedBy: node._lastEditedBy || null,
    _lastEditedAt: node._lastEditedAt || null,
    // Document reference (symlink to another document)
    linkedDocId: node.linkedDocId || null,
    linkedDocTitle: node.linkedDocTitle || null,
    // Tags (array of tag definition IDs)
    tags: node.tags || null,
  });
}

/**
 * Converts a flat node back to tree structure (without children populated)
 * @param {Object} flatNode - Flat node from Y.Map
 * @returns {Object} Tree node structure
 */
export function flatNodeToTree(flatNode) {
  const node = {
    id: flatNode.id,
    name: flatNode.name || "",
    children: [], // Will be populated by buildTree
  };

  // Copy optional properties if they exist
  if (flatNode.createdAt) node.createdAt = flatNode.createdAt;
  if (flatNode.color) node.color = flatNode.color;
  if (flatNode.notes?.length) node.notes = flatNode.notes;
  if (flatNode.status) node.status = flatNode.status;
  if (flatNode.startDate) node.startDate = flatNode.startDate;
  if (flatNode.endDate) node.endDate = flatNode.endDate;
  if (flatNode.duration) node.duration = flatNode.duration;
  if (flatNode.effort) node.effort = flatNode.effort;
  if (flatNode.effortUnit) node.effortUnit = flatNode.effortUnit;
  if (flatNode.priority) node.priority = flatNode.priority;
  if (flatNode.assignee) node.assignee = flatNode.assignee;
  if (flatNode.link) node.link = flatNode.link;
  if (flatNode.x !== undefined) node.x = flatNode.x;
  if (flatNode.y !== undefined) node.y = flatNode.y;
  if (flatNode.mmX !== undefined) node.mmX = flatNode.mmX;
  if (flatNode.mmY !== undefined) node.mmY = flatNode.mmY;
  if (flatNode.mmSide !== undefined) node.mmSide = flatNode.mmSide;
  if (flatNode.mmOffsetX !== undefined) node.mmOffsetX = flatNode.mmOffsetX;
  if (flatNode.mmOffsetY !== undefined) node.mmOffsetY = flatNode.mmOffsetY;
  // Mindmap dimension caching (prevents infinite recalculation loop)
  if (flatNode.mmWidth !== undefined) node.mmWidth = flatNode.mmWidth;
  if (flatNode.mmHeight !== undefined) node.mmHeight = flatNode.mmHeight;
  if (flatNode.mmTextHash !== undefined) node.mmTextHash = flatNode.mmTextHash;
  if (flatNode.collapsed !== undefined) node.collapsed = flatNode.collapsed;
  if (flatNode._lastEditedBy) node._lastEditedBy = flatNode._lastEditedBy;
  if (flatNode._lastEditedAt) node._lastEditedAt = flatNode._lastEditedAt;
  if (flatNode.estimate) node.estimate = flatNode.estimate;
  if (flatNode.linkedDocId) node.linkedDocId = flatNode.linkedDocId;
  if (flatNode.linkedDocTitle) node.linkedDocTitle = flatNode.linkedDocTitle;
  if (flatNode.tags?.length) node.tags = flatNode.tags;

  return copyInterfaceFields(flatNode, node);
}

export function schemaVersion(doc) {
  return doc.getMap("metadata").get("schemaVersion") === SCHEMA_V2 ? SCHEMA_V2 : 1;
}

/** The fields of one v2 node (a nested Y.Map), or null if the value is not one. */
export function v2Fields(value) {
  return value instanceof Y.Map ? value.toJSON() : null;
}

/** Live v2 nodes: id → fields (with `id`). Deleted ids are left out. */
export function readV2Nodes(doc) {
  const deleted = doc.getMap(DELETED_NODES);
  const flat = new Map();
  doc.getMap(NODES_V2).forEach((value, id) => {
    if (deleted.has(id)) return;
    const fields = v2Fields(value);
    if (fields) flat.set(id, { ...fields, id });
  });
  return flat;
}

/**
 * Where each v1 node shows: one place per node, never a cycle (the
 * safeguards of 2026-10-02, docs/internal/2026-10-02-convergence-findings.md).
 * - A node shows under its own `parentId` when that parent lists it; else
 *   under the first parent reached from the root that lists it.
 * - A node met again is not followed (a cycle), so the tree is finite.
 * - Nodes no shown parent reaches are orphans: shown under their `parentId`
 *   if it is shown, else under the root, with their subtree.
 * @param {Y.Map} nodes - the v1 `nodes` map
 * @returns {{children: Map<string, string[]>, orphans: Array<{id: string, parentId: string}>}}
 */
export function placeV1(nodes, rootId) {
  const children = new Map();
  const orphans = [];
  if (!rootId || !nodes.has(rootId)) return { children, orphans };
  // Which parents list each node (one pass, so placement stays linear).
  const listedBy = new Map();
  nodes.forEach((flatNode, id) => {
    for (const childId of flatNode?.childIds || []) {
      if (!listedBy.has(childId)) listedBy.set(childId, new Set());
      listedBy.get(childId).add(id);
    }
  });
  const ownParent = (id) => {
    const parentId = nodes.get(id)?.parentId;
    return parentId && listedBy.get(id)?.has(parentId) ? parentId : null;
  };
  const place = (id) => {
    const shown = [];
    children.set(id, shown);
    for (const childId of nodes.get(id)?.childIds || []) {
      if (children.has(childId) || !nodes.has(childId)) continue;
      const own = ownParent(childId);
      if (own && own !== id) continue; // shown under its own parent
      shown.push(childId);
      place(childId);
    }
  };
  place(rootId);
  // What the root does not reach: under its parent if shown, else the root.
  const unplaced = [...nodes.keys()].filter((id) => !children.has(id)).sort();
  for (const id of unplaced) {
    if (children.has(id)) continue; // reached through an earlier orphan
    const parentId = nodes.get(id)?.parentId;
    const target = parentId && children.has(parentId) ? parentId : rootId;
    children.get(target).push(id);
    orphans.push({ id, parentId: target });
    place(id);
  }
  return { children, orphans };
}

/**
 * Where each v2 node shows.
 * - Under its `parentId`, siblings sorted by `order` then id.
 * - Deleted with its parent: a node whose parent (or an ancestor) is in
 *   `deletedNodes` is hidden (decision 3).
 * - Under the root when its parent is missing (not arrived yet, or never
 *   existed): `detached`, reason "orphan".
 * - Under the root when it is the smallest id of a cycle: `detached`,
 *   reason "cycle"; the rest of the cycle shows under it.
 * @param {Map<string, Object>} flat - live nodes (readV2Nodes)
 * @param {string} rootId
 * @param {{has: (id: string) => boolean}} deleted - deleted ids
 * @returns {{children: Map<string, string[]>, parents: Map<string, string>,
 *   hidden: Set<string>, detached: Array<{id: string, reason: string}>}}
 */
export function placeV2(flat, rootId, deleted) {
  const children = new Map();
  const parents = new Map();
  const hidden = new Set();
  const detached = [];
  if (!rootId || !flat.has(rootId)) return { children, parents, hidden, detached };
  // The parent each node shows under, before cycles: null = hidden.
  const status = new Map([[rootId, "shown"]]);
  const shownParent = new Map();
  const ids = [...flat.keys()].sort();
  for (const start of ids) {
    while (!status.has(start)) {
      // Follow the parents up to something known: shown, hidden, missing or a loop.
      const chain = [];
      const onChain = new Set();
      let id = start;
      let result = null;
      for (;;) {
        if (status.has(id)) { result = status.get(id); break; }
        if (onChain.has(id)) {
          // A cycle: its smallest id goes under the root, then walk again.
          const cycle = chain.slice(chain.indexOf(id));
          const breaker = cycle.reduce((a, b) => (b < a ? b : a));
          status.set(breaker, "shown");
          shownParent.set(breaker, rootId);
          detached.push({ id: breaker, reason: "cycle" });
          break;
        }
        chain.push(id);
        onChain.add(id);
        const parentId = flat.get(id).parentId;
        if (parentId && deleted.has(parentId)) { result = "hidden"; break; }
        if (!parentId || parentId === id || !flat.has(parentId)) {
          // Missing parent: this node goes under the root with what is under it.
          status.set(id, "shown");
          shownParent.set(id, rootId);
          detached.push({ id, reason: "orphan" });
          chain.pop();
          result = "shown";
          break;
        }
        id = parentId;
      }
      if (result === null) continue; // a cycle was broken: walk again
      for (const node of chain) {
        status.set(node, result);
        if (result === "shown" && !shownParent.has(node)) shownParent.set(node, flat.get(node).parentId);
      }
    }
  }
  for (const id of ids) {
    if (status.get(id) === "hidden") { hidden.add(id); continue; }
    children.set(id, []);
  }
  for (const [id, parentId] of shownParent) {
    if (id === rootId) continue;
    parents.set(id, parentId);
    children.get(parentId).push(id);
  }
  const sortKey = (id) => {
    const order = flat.get(id).order;
    return { id, order: isValidOrderKey(order) ? order : "" };
  };
  for (const [id, list] of children) {
    if (list.length < 2) continue;
    children.set(id, list.map(sortKey).sort(compareSiblings).map((entry) => entry.id));
  }
  return { children, parents, hidden, detached };
}

/**
 * A map's nodes and where each shows, in either schema. For readers outside
 * the app's map manager (export, recovery); the manager has its own copy
 * with local fields and caching.
 * @param {Y.Doc} doc
 * @returns {{schema: number, rootId: string|null, flat: Map<string, Object>,
 *   children: Map<string, string[]>}}
 */
export function readMap(doc) {
  const rootId = doc.getMap("metadata").get("rootId") || null;
  if (schemaVersion(doc) === SCHEMA_V2) {
    const flat = readV2Nodes(doc);
    const { children } = placeV2(flat, rootId, doc.getMap(DELETED_NODES));
    // Per-device fields: the map has none of its own; v1 kept a copy.
    const v1 = doc.getMap(NODES_V1);
    for (const [id, node] of flat) {
      const old = v1.get(id);
      if (!old) continue;
      for (const field of LOCAL_FIELDS) if (old[field] !== undefined && node[field] === undefined) node[field] = old[field];
    }
    return { schema: SCHEMA_V2, rootId, flat, children };
  }
  const nodes = doc.getMap(NODES_V1);
  const flat = new Map();
  nodes.forEach((value, id) => { if (value && typeof value === "object") flat.set(id, { ...value, id: value.id || id }); });
  const { children } = placeV1(nodes, rootId);
  return { schema: 1, rootId, flat, children };
}

/**
 * The tree of a map in either schema, each node made by `toNode(flat)`
 * (its `children` are filled in). Null when the map has no readable root.
 * @param {Y.Doc} doc
 * @param {(flat: Object) => Object} [toNode]
 */
export function readTree(doc, toNode = (flat) => ({ ...flat })) {
  const { rootId, flat, children } = readMap(doc);
  if (!rootId || !flat.has(rootId) || !children.has(rootId)) return null;
  const build = (id) => {
    const node = toNode(flat.get(id));
    node.children = (children.get(id) || []).map(build);
    return node;
  };
  return build(rootId);
}

/** Number of nodes a doc holds in either schema (0 = nothing yet). */
export function nodeCountOf(doc) {
  return schemaVersion(doc) === SCHEMA_V2 ? readV2Nodes(doc).size : doc.getMap(NODES_V1).size;
}
